// `/debug` › Songstats — the Songstats chain end to end, plus the live probe and the force-sync actions.
//
// Every link in the Songstats chain fails SILENTLY, and from the dashboard they are indistinguishable. An unset
// vendor key short-circuits with no call and no error. A band without the Spotify artist-id claim never gets a
// schedule. The cadence window defers the job under a healthy status. A payload that lands mis-keyed stamps a FRESH
// `as_of` while every reader null-degrades to an em-dash. So the failure is diagnosed from the chain, not the
// symptom: this tab puts the chain in the order it runs, and adds what the vendor answers RIGHT NOW (the probe,
// which lands nothing) and what our shaper makes of that answer.
import { useState, type ReactNode } from "react";
import { Button, Card, Tag } from "@cntrl/ui";
import { useBandContext } from "../../app/bandContext.js";
import { ApiError, errorMessage } from "../../app/hooks/apiError.js";
import { ENV_NAME } from "../../config/env.js";
import {
  useSongstatsDiagnostics,
  useSongstatsProbe,
  useSongstatsRefresh,
  type ChainVerdict,
  type ProbeCall,
  type SongstatsProbeDoc,
  type VerdictStatus,
} from "../../app/hooks/useSongstatsDebug.js";
import styles from "./DebugScreen.module.scss";

const TONE: Record<VerdictStatus, "success" | "warning" | "danger"> = {
  ok: "success",
  warn: "warning",
  fail: "danger",
};

/** Pretty-print a JSON string for display; a non-JSON body is shown verbatim rather than swallowed. */
function pretty(json: string | null | undefined): string {
  if (!json) return "";
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}

const stamp = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : "—");

/** A collapsible raw-JSON well. Collapsed by default: these payloads run to hundreds of lines, and the
 * page's job is to make the CHAIN readable, with the evidence one click away. */
function JsonBlock({ label, json, meta }: { label: string; json: string | null | undefined; meta?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const text = pretty(json);
  return (
    <div className={styles.jsonBlock}>
      <div className={styles.jsonHead}>
        <button type="button" className={styles.jsonToggle} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? "▾" : "▸"} {label}
        </button>
        <span className={styles.jsonMeta}>
          {meta}
          {text.length > 0 && <span>{text.length.toLocaleString()} chars</span>}
        </span>
        {text.length > 0 && (
          <Button
            variant="ghost"
            size="xs"
            onClick={() => void navigator.clipboard?.writeText(text)}
          >
            Copy
          </Button>
        )}
      </div>
      {open && <pre className={styles.json}>{text || "(empty)"}</pre>}
    </div>
  );
}

/** The chain, as traffic lights. This is the part to read first; everything below it is evidence. */
function Verdicts({ verdicts }: { verdicts: ChainVerdict[] }) {
  return (
    <ol className={styles.verdicts}>
      {verdicts.map((v, i) => (
        <li key={`${v.step}-${v.name}-${i}`} className={styles.verdict}>
          <span className={styles.verdictStep}>{v.step}</span>
          <div className={styles.verdictBody}>
            <div className={styles.verdictTitle}>
              <strong>{v.name}</strong>
              <Tag tone={TONE[v.status]} size="sm">
                {v.status}
              </Tag>
            </div>
            <p className={styles.verdictDetail}>{v.detail}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

/** A label/value definition grid — the shape most of these sections are. */
function Facts({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className={styles.facts}>
      {rows.map(([label, value]) => (
        <div key={label} className={styles.fact}>
          <dt>{label}</dt>
          <dd>{value ?? "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

function ProbeCallRow({ call }: { call: ProbeCall }) {
  const failed = call.outcome !== "Success" || (call.httpStatus !== null && call.httpStatus >= 400);
  return (
    <div className={styles.probeCall}>
      <div className={styles.probeCallHead}>
        <strong>{call.key}</strong>
        {call.primary && (
          <Tag tone="neutral" size="sm" title="This read GATES the whole fetch — if it fails, the sync reschedules.">
            primary
          </Tag>
        )}
        <Tag tone={failed ? "danger" : "success"} size="sm">
          {call.httpStatus ?? call.outcome}
        </Tag>
        <span className={styles.jsonMeta}>
          {call.elapsedMs}ms · {call.bytes.toLocaleString()} bytes
        </span>
      </div>
      <code className={styles.url}>{call.url}</code>
      {call.error && <p className={styles.error}>{call.error}</p>}
      {failed && (call.retryAfter || call.server || call.cfRay) && (
        <p className={styles.keys}>
          {call.retryAfter && (
            <>
              retry-after: <code>{call.retryAfter}</code>{" · "}
            </>
          )}
          {call.server && (
            <>
              server: <code>{call.server}</code>{" · "}
            </>
          )}
          {call.cfRay && (
            <>
              cf-ray: <code>{call.cfRay}</code>
            </>
          )}
        </p>
      )}
      {failed && call.cfRay && (
        <p className={styles.notice}>
          A <code>cf-ray</code> on a refusal means a Cloudflare edge answered — the call may never have reached
          Songstats&apos; own per-key accounting, which is a different problem from the key being rate limited.
        </p>
      )}
      {call.topLevelKeys.length > 0 && (
        <p className={styles.keys}>
          top-level keys: <code>{call.topLevelKeys.join(", ")}</code>
        </p>
      )}
      <JsonBlock label="Raw response body" json={call.body} />
    </div>
  );
}

function ProbeResult({ probe }: { probe: SongstatsProbeDoc }) {
  return (
    <div className={styles.stack}>
      <Facts
        rows={[
          ["Ran at", stamp(probe.generatedOnUtc)],
          ["Base URL", <code key="u">{probe.baseUrl}</code>],
          ["Artist id", probe.artistId ? <code key="a">{probe.artistId}</code> : "—"],
          ["Key configured", probe.keyConfigured ? "yes" : "no"],
        ]}
      />
      {probe.notice && <p className={styles.notice}>{probe.notice}</p>}
      {probe.skipped && <p className={styles.notice}>{probe.skipped}</p>}

      {probe.calls.map((call) => (
        <ProbeCallRow key={call.key} call={call} />
      ))}

      {probe.shaped && (
        <>
          <h3 className={styles.subhead}>What the shaper made of it</h3>
          <p className={styles.hint}>
            This is the real <code>SongstatsPayloadShaper</code> over the bodies just fetched — exactly what a
            sync would have written, without writing it.
          </p>
          <Verdicts verdicts={probe.shaped.verdicts} />
          <Facts
            rows={[
              ["Shaped doc keys", <code key="k">{probe.shaped.platformDocKeys.join(", ") || "(none)"}</code>],
              ["Snapshots derived", probe.shaped.snapshots.length],
              ["Cities shaped", probe.shaped.cityCount],
              ["Radio doc", probe.shaped.radioDoc ? probe.shaped.radioDocKeys.join(", ") : "none (landing skipped)"],
            ]}
          />
          <JsonBlock label="Shaped platform doc (what would land as platform_stats_current)" json={probe.shaped.platformDoc} />
          <JsonBlock label="Combined raw payload (what would land in vendor_payloads)" json={probe.shaped.combinedPayload} />
        </>
      )}
    </div>
  );
}

export function SongstatsPanel() {
  const { bandId } = useBandContext();
  const diagnostics = useSongstatsDiagnostics();
  const probe = useSongstatsProbe();
  const refresh = useSongstatsRefresh();
  const [action, setAction] = useState<string | null>(null);

  const runProbe = () => {
    setAction(null);
    probe.mutate(undefined, {
      onError: (err) => setAction(errorMessage(err, "The probe failed.")),
    });
  };

  const runRefresh = (clearContentHash: boolean) => {
    setAction(null);
    refresh.mutate(
      { clearContentHash },
      {
        onSuccess: (result) => setAction(result.detail),
        onError: (err) => setAction(errorMessage(err, "The refresh failed.")),
      },
    );
  };

  const doc = diagnostics.data;
  const notFound = diagnostics.error instanceof ApiError && diagnostics.error.status === 404;

  return (
    <>
      <div className={styles.actions}>
        <Button variant="ghost" size="sm" onClick={() => void diagnostics.refetch()} disabled={diagnostics.isFetching}>
          {diagnostics.isFetching ? "Reading…" : "Re-read"}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => runRefresh(false)} disabled={refresh.isPending}>
          Force sync
        </Button>
        <Button variant="ghost" size="sm" onClick={() => runRefresh(true)} disabled={refresh.isPending}>
          Force sync + clear hash
        </Button>
        <Button variant="primary" size="sm" onClick={runProbe} disabled={probe.isPending}>
          {probe.isPending ? "Calling Songstats…" : "Probe vendor live"}
        </Button>
      </div>

      <p className={styles.hint}>
        Band <code>{bandId}</code> · env <code>{ENV_NAME}</code>. Every link in the Songstats chain fails
        silently and they all look the same from the dashboard, so this reads the chain end to end. “Probe
        vendor live” calls Songstats now and shapes the answer <strong>without landing anything</strong>;
        “Force sync” only makes the schedules due — the scheduler still owns the claim, and the cadence gates
        still apply. “Clear hash” additionally drops the change-detection baseline so an unchanged payload
        re-lands.
      </p>

      {action && <p className={styles.notice}>{action}</p>}

      {notFound && (
        <Card title="Not available in this environment">
          <p>
            The API refuses this diagnostic unless <code>EnvName</code> is a recognised non-production lane
            (<code>LOCAL</code>, <code>DEV</code>, <code>QA</code>) — an unrecognised or unset value refuses
            too, so a novel environment hides the surface rather than exposing it.
          </p>
          <p className={styles.hint}>
            Seeing this in a lane you expected to work means the API and this page disagree about the
            environment: the route is only mounted client-side under the same allowlist, so reaching the page
            at all means the SPA thinks it is non-production while the server does not.
          </p>
        </Card>
      )}

      {diagnostics.isError && !notFound && (
        <Card title="Could not read the diagnostic">
          <p className={styles.error}>{errorMessage(diagnostics.error, "Request failed.")}</p>
        </Card>
      )}

      {diagnostics.isLoading && <Card title="Reading the chain…" />}

      {doc && (
        <>
          <Card title="The chain" subtitle={`Generated ${stamp(doc.generatedOnUtc)}`}>
            <Verdicts verdicts={doc.verdicts} />
          </Card>

          <Card title="1 · Vendor key">
            <Facts
              rows={[
                ["Key configured", doc.config.keyConfigured ? `yes (${doc.config.keyFingerprint}, ${doc.config.keyLength} chars)` : "NO"],
                ["Base URL", <code key="u">{doc.config.baseUrl}</code>],
                ["Kill switch", doc.config.providerPaused ? "PAUSED" : "not paused"],
              ]}
            />
          </Card>

          <Card title="2 · Spotify artist-id claim" subtitle="The only key Songstats is ever called with">
            <Facts
              rows={[
                ["Connection", doc.claim.connectionPresent ? (doc.claim.status ?? "present") : "none"],
                ["Artist id", doc.claim.externalAccountId ? <code key="a">{doc.claim.externalAccountId}</code> : "NOT SET"],
                ["Handle", doc.claim.displayHandle],
                ["Connected", stamp(doc.claim.connectedOn)],
                ["Last sync", stamp(doc.claim.lastSyncOn)],
                ["Last error", doc.claim.lastError],
              ]}
            />
          </Card>

          <Card title="3 · Sync schedules" subtitle="No row means the job never runs, whatever else is healthy">
            {doc.schedules.length === 0 ? (
              <p className={styles.error}>No sync_schedules rows for this band at all.</p>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Provider</th>
                      <th>Kind</th>
                      <th>Last run</th>
                      <th>Status</th>
                      <th>Next run</th>
                      <th>Hash</th>
                      <th>State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doc.schedules.map((s) => (
                      <tr key={`${s.provider}/${s.kind}`}>
                        <td>{s.provider}</td>
                        <td>{s.kind}</td>
                        <td>{stamp(s.lastRunOn)}</td>
                        <td title={s.lastError ?? undefined}>{s.lastStatus ?? "—"}</td>
                        <td>{stamp(s.nextRunOn)}</td>
                        <td>
                          <code>{s.contentHashPrefix ?? "—"}</code>
                        </td>
                        <td>
                          <Tag tone={s.paused ? "danger" : s.due ? "success" : "neutral"} size="sm">
                            {s.paused ? "paused" : s.due ? "due" : "waiting"}
                          </Tag>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card title="4 · Cadence gates" subtitle="Evaluated by the real SyncCadencePolicy against the real schedule row">
            <Facts
              rows={[
                ["Verdict now", <strong key="v">{doc.cadence.preFetchVerdict}</strong>],
                ["Window", `opens ${String(doc.cadence.windowOpenHourUtc).padStart(2, "0")}:00 UTC · now ${String(doc.cadence.currentHourUtc).padStart(2, "0")}:00 · ${doc.cadence.windowOpen ? "OPEN" : "CLOSED"}`],
                ["Budget", `${doc.cadence.spentToday} / ${doc.cadence.dailyCap} calls today`],
                ["Min interval", `${doc.cadence.minIntervalMinutes} min`],
                ["Would run at", stamp(doc.cadence.wouldRunAt)],
              ]}
            />
          </Card>

          <Card title="5 · Raw vendor payloads" subtitle="integrations.vendor_payloads — proof the vendor answered">
            {doc.rawPayloads.length === 0 ? (
              <p className={styles.error}>No rows — Songstats has never been successfully called for this band.</p>
            ) : (
              <div className={styles.stack}>
                {doc.rawPayloads.map((p, i) => (
                  <JsonBlock
                    key={`${p.endpoint}-${p.fetchedOn}-${i}`}
                    label={`${p.endpoint} · ${stamp(p.fetchedOn)}`}
                    json={p.payload}
                    meta={<span>{p.bytes.toLocaleString()} bytes · hash {p.contentHashPrefix ?? "—"}</span>}
                  />
                ))}
              </div>
            )}
          </Card>

          <Card title="6 · What landed" subtitle="analytics.* — the rows every reader serves from">
            <div className={styles.stack}>
              {doc.landings.platformDocs.length === 0 ? (
                <p className={styles.error}>No platform_stats_current rows at all.</p>
              ) : (
                doc.landings.platformDocs.map((p) => (
                  <div key={p.platform}>
                    <p className={styles.keys}>
                      <strong>{p.platform}</strong> · as_of {stamp(p.asOf)} · keys <code>{p.topLevelKeys.join(", ")}</code>
                    </p>
                    <JsonBlock label={`platform_stats_current · ${p.platform}`} json={p.payload} />
                  </div>
                ))
              )}

              {doc.landings.radio && (
                <div>
                  <p className={styles.keys}>
                    <strong>radio</strong> · as_of {stamp(doc.landings.radio.asOf)} · keys{" "}
                    <code>{doc.landings.radio.topLevelKeys.join(", ")}</code>
                  </p>
                  <JsonBlock label="radio_stats_current" json={doc.landings.radio.payload} />
                </div>
              )}

              <Facts
                rows={[
                  ["Audience cities", `${doc.landings.audienceCityCount} rows (as_of ${doc.landings.audienceCitiesAsOf ?? "—"})`],
                  ["Stat snapshots", `${doc.landings.snapshotCount} rows (as_of ${doc.landings.snapshotsAsOf ?? "—"})`],
                  ["Track stats", `${doc.landings.trackStatCount} rows`],
                ]}
              />

              {doc.landings.snapshotsLatest.length > 0 && (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Platform</th>
                        <th>Metric</th>
                        <th>Value</th>
                        <th>As of</th>
                      </tr>
                    </thead>
                    <tbody>
                      {doc.landings.snapshotsLatest.map((s, i) => (
                        <tr key={`${s.metric}-${s.asOf}-${i}`}>
                          <td>{s.platform ?? "—"}</td>
                          <td>{s.metric}</td>
                          <td>{s.value.toLocaleString()}</td>
                          <td>{s.asOf ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {doc.landings.audienceCitiesSample.length > 0 && (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>City</th>
                        <th>Region</th>
                        <th>Country</th>
                        <th>Audience</th>
                      </tr>
                    </thead>
                    <tbody>
                      {doc.landings.audienceCitiesSample.map((c, i) => (
                        <tr key={`${c.city}-${i}`}>
                          <td>{c.city}</td>
                          <td>{c.region}</td>
                          <td>{c.country}</td>
                          <td>{c.audience.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {doc.landings.trackStatsSample.length > 0 && (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Spotify id</th>
                        <th>Pop.</th>
                        <th>28D</th>
                        <th>Lifetime</th>
                      </tr>
                    </thead>
                    <tbody>
                      {doc.landings.trackStatsSample.map((t) => (
                        <tr key={t.trackKey}>
                          <td>{t.title ?? "—"}</td>
                          <td>
                            <code>{t.spotifyTrackId ?? "—"}</code>
                          </td>
                          <td>{t.popularity ?? "—"}</td>
                          <td>{t.streams28d?.toLocaleString() ?? "—"}</td>
                          <td>{t.streamsLifetime?.toLocaleString() ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Card>

          <Card
            title="7 · What the surfaces compose"
            subtitle="The exact documents /dashboard/analytics and /music return — where a healthy landing goes to die"
          >
            <div className={styles.stack}>
              <JsonBlock label="GET /bands/{bandId}/dashboard/analytics" json={JSON.stringify(doc.reads.dashboard)} />
              <JsonBlock label="GET /bands/{bandId}/music" json={JSON.stringify(doc.reads.music)} />
            </div>
          </Card>
        </>
      )}

      {probe.data && (
        <Card title="Live vendor probe" subtitle="Called just now · nothing was written">
          <ProbeResult probe={probe.data} />
        </Card>
      )}
    </>
  );
}
